import cx from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

// Multibrand-UI Components
import Button from '@zg-rentals/multibrand-ui/lib/Button';
import Input from '@zg-rentals/multibrand-ui/lib/Input';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import SVEmailInput from '@zg-rentals/multibrand-ui/lib/SVEmailInput';
import Notification from '@zg-rentals/multibrand-ui/lib/Notification';

// components
import Autocomplete from 'app/shared/components/Autocomplete';

// constants
import NotificationConstants from 'app/shared/constants/NotificationConstants';
import StringConstants from 'app/shared/constants/StringConstants';

// rmw context
import { RMWContext } from 'app/shared/providers/RMWContext';

// actions
import AddressActions from 'app/shared/actions/AddressActions';
import MarketingActions from 'app/shared/actions/MarketingActions';

// selectors
import { UserSelectors, RoutingSelectors } from 'app/shared/selectors';

// utils
import queryUtils from 'app/shared/utils/queryUtils';
import pathsManifest from 'app/shared/pathsManifest';
import analyticsUtils from 'app/client/utils/analyticsUtils';


// stylesheets
import './style.scss';

const propTypes = {
    addressList: PropTypes.array,
};

const defaultProps = {
    addressList: [],
};

class PageHeroAutocomplete extends React.Component {
    static contextType = RMWContext;

    constructor(props) {
        super(props);

        this.state = {
            fullAddress: '',
            unitNumber: '',
            propertyId: '',
            statusMessage: '',
            savedStatus: NotificationConstants.SUCCESS,
            emailError: '',
            email: '',
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleTriggerAction = this.handleTriggerAction.bind(this);
        this.selectAddress = this.selectAddress.bind(this);
        this.updateAddress = this.updateAddress.bind(this);
        this.updateUnit = this.updateUnit.bind(this);
        this.updateEmail = this.updateEmail.bind(this);
        this.updateStatusMessage = this.updateStatusMessage.bind(this);
    }

    selectAddress(address) {
        this.setState({
            fullAddress: address.name,
            propertyId: address.propertyId,
        });
    }

    updateAddress(value) {
        this.setState({
            fullAddress: value,
        });
    }

    handleSubmit() {
        const { dispatch, queryString } = this.props;
        const {
            emailError,
            propertyId,
            email,
            fullAddress,
            unitNumber,
        } = this.state;

        this.setState({
            statusMessage: '',
        });

        const parsedQuery = queryUtils.parse(queryString);

        const {
            utm_source: utmSource,
            utm_medium: utmMedium,
            utm_campaign: utmCampaign,
            utm_content: utmContent,
        } = parsedQuery;

        if (!fullAddress) {
            this.updateStatusMessage('Must specify an address and city, state, or zip', NotificationConstants.ERROR);
            return;
        }

        if (!emailError) {
            // fetch address details
            dispatch(AddressActions.getDetails({ address: fullAddress, unit: unitNumber }))
                .then(() => {
                    const paths = pathsManifest.getAllPaths();
                    const queryParams = queryUtils.stringify({ fullAddress, unitNumber, propertyId });
                    const path = `${paths.zillowPriceRentalResultsPage}${queryParams}`;

                    window.dataLayer.push({ event: 'RentZestimate' });
                    window.router.transitionTo(path);
                })
                .catch((errorRMWModel) => {
                    this.updateStatusMessage(errorRMWModel.message, NotificationConstants.ERROR);
                });

            analyticsUtils.track.optimizelyCTAEvent();

            dispatch(MarketingActions.createMarketingLead({
                fullAddress,
                unit: unitNumber,
                marketingLeadType: 'paidInclusionRentEstimator',
                email,
                utmSource,
                utmMedium,
                utmCampaign,
                utmContent,
            }));
        }
    }

    handleTriggerAction() {
        this.props.dispatch(AddressActions.autocomplete(this.state.fullAddress));
    }

    updateUnit(e) {
        const unitNumber = e.target.value;
        const unitLengthLimit = 20;

        if (unitNumber.length <= unitLengthLimit) {
            this.setState({ unitNumber });
        }
    }

    updateEmail(payload, method) {
        if (method === 'onFocus') delete payload.errorMessage;
        const { value, errorMessage } = payload;
        this.setState({
            email: value,
            emailError: errorMessage,
        });
    }

    getNotificationType() {
        const { brand } = this.context;
        const { savedStatus } = this.state;

        let notificationStatus = savedStatus || 'info';

        // if brand is zillow set success type to be info type
        if (brand === StringConstants.UNFORMATTED_BRAND_NAMES.zillow) {
            notificationStatus =
                notificationStatus === NotificationConstants.SUCCESS ? NotificationConstants.INFO : notificationStatus;
        }

        return notificationStatus.toLowerCase();
    }

    updateStatusMessage(message, status = null) {
        this.setState({
            statusMessage: message,
            savedStatus: status,
        });
    }

    render() {
        const {
            unitNumber,
            fullAddress,
            statusMessage,
            savedStatus,
            email,
            emailError,
        } = this.state;

        const { addressList, storedEmail } = this.props;

        const notificationType = this.getNotificationType();


        return (
            <div className="PageHeroAutocomplete">
                <div className="PageHeroAutocomplete-address stacked-sm">
                    <Autocomplete
                        className="PageHeroAutocomplete-street-input inline-md"
                        itemList={addressList}
                        placeholder="Street address"
                        value={fullAddress}
                        onChange={this.updateAddress}
                        onClick={this.selectAddress}
                        onTriggerAction={this.handleTriggerAction}
                        minKeyStrokes={7}
                    />
                    <Input
                        className="PageHeroAutocomplete-unit-input"
                        placeholder="Unit"
                        value={unitNumber}
                        onChange={this.updateUnit}
                    />
                </div>
                {!storedEmail && (
                    <div>
                        <SVEmailInput
                            id="PageHeroAutocomplete-email"
                            className="stacked-sm"
                            placeholder="Email"
                            label=""
                            type="email"
                            value={email}
                            errorMessage={emailError}
                            onChange={payload => this.updateEmail(payload, 'onBlur')}
                            onBlur={payload => this.updateEmail(payload, 'onBlur')}
                            onFocus={payload => this.updateEmail(payload, 'onFocus')}
                        />
                        <Text type="body-tiny" className="PageHeroAutocomplete-disclaimer stacked-sm">
                            By providing your email address, you agree to receive promotional and marketing
                             materials from Zillow.
                        </Text>
                    </div>
                )}
                <Button
                    type="primary"
                    onClick={this.handleSubmit}
                    className="stacked-sm"
                >
                    Continue
                </Button>
                <Text type="body-tiny" className="PageHeroAutocomplete-error">
                    {statusMessage}
                </Text>
                {/* is this even used? */}
                <div className="Notification-Toast-Container">
                    <Notification
                        type={notificationType}
                        className={cx({
                            'Notification-opacity': statusMessage.length > 0,
                            'Notification-done': savedStatus === NotificationConstants.SUCCESS,
                        })}
                    >
                        {statusMessage}
                    </Notification>
                </div>
            </div>
        );
    }
}

PageHeroAutocomplete.propTypes = propTypes;
PageHeroAutocomplete.defaultProps = defaultProps;

const mapStateToProps = state => ({
    queryString: RoutingSelectors.makeSearch(state),
    storedEmail: UserSelectors.makeEmail(state),
});

export default connect(mapStateToProps)(PageHeroAutocomplete);
